import { useState, type FormEvent, type ReactNode } from "react";
import { useAuth } from "../auth/auth";
import { answerMfa, completeNewPassword, confirmForgotPassword, forgotPassword, signIn, type SignInStep } from "../auth/cognito";
import { config } from "../config";
import { Button } from "./ui";

type View =
  | { kind: "credentials" }
  | { kind: "new_password"; session: string; email: string }
  | { kind: "mfa"; session: string; email: string }
  | { kind: "forgot"; email: string }
  | { kind: "forgot_confirm"; email: string };

/** Our sign-in page (MVP-G6). One card, one question at a time, our type and spacing.
 *  Cognito is behind it: temporary-password change on first login, optional authenticator
 *  code, and the forgot-password flow all happen here. The hosted page is a fallback link. */
export function SignIn() {
  const auth = useAuth();
  const c = config();
  const [view, setView] = useState<View>({ kind: "credentials" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (f: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await f();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const land = (s: SignInStep) => {
    if (s.kind === "done") auth.setTokens(s.tokens);
    else if (s.kind === "new_password") setView({ kind: "new_password", session: s.session, email: s.email });
    else setView({ kind: "mfa", session: s.session, email: s.email });
  };
  const ready = !!c.cognito_region && !!c.cognito_client_id;

  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="card w-full max-w-[420px] px-9 py-10">
        <img src="/humindly-logo.png" alt="Humindly" className="h-7 mx-auto mb-8" />

        {view.kind === "credentials" && (
          <Form
            title="Sign in"
            sub="Your work e-mail and password."
            busy={busy}
            error={error}
            notice={notice}
            submitLabel="Sign in →"
            onSubmit={(d) => run(async () => land(await signIn(c.cognito_region, c.cognito_client_id, d.email, d.password)))}
            fields={[
              { name: "email", label: "E-mail", type: "email", autoComplete: "username", autoFocus: true },
              { name: "password", label: "Password", type: "password", autoComplete: "current-password" },
            ]}
            footer={
              <button type="button" className="link" onClick={() => { setError(null); setNotice(null); setView({ kind: "forgot", email: "" }); }}>
                Forgot your password?
              </button>
            }
            disabled={!ready}
          />
        )}

        {view.kind === "new_password" && (
          <Form
            title="Choose your password"
            sub="You signed in with a temporary password. Pick the one you will use from now on."
            busy={busy}
            error={error}
            submitLabel="Save and continue →"
            onSubmit={(d) =>
              run(async () => {
                if (d.password !== d.confirm) throw new Error("The two passwords do not match.");
                land(await completeNewPassword(c.cognito_region, c.cognito_client_id, view.email, view.session, d.password));
              })
            }
            fields={[
              { name: "password", label: "New password", type: "password", autoComplete: "new-password", autoFocus: true, hint: "At least 12 characters, with upper and lower case, a number and a symbol." },
              { name: "confirm", label: "Again, to be sure", type: "password", autoComplete: "new-password" },
            ]}
          />
        )}

        {view.kind === "mfa" && (
          <Form
            title="One more step"
            sub="Enter the six-digit code from your authenticator app."
            busy={busy}
            error={error}
            submitLabel="Continue →"
            onSubmit={(d) => run(async () => land(await answerMfa(c.cognito_region, c.cognito_client_id, view.email, view.session, d.code)))}
            fields={[{ name: "code", label: "Code", type: "text", autoComplete: "one-time-code", autoFocus: true, inputMode: "numeric" }]}
          />
        )}

        {view.kind === "forgot" && (
          <Form
            title="Reset your password"
            sub="We will e-mail you a code."
            busy={busy}
            error={error}
            submitLabel="Send the code →"
            onSubmit={(d) =>
              run(async () => {
                await forgotPassword(c.cognito_region, c.cognito_client_id, d.email);
                setView({ kind: "forgot_confirm", email: d.email });
              })
            }
            fields={[{ name: "email", label: "E-mail", type: "email", autoComplete: "username", autoFocus: true, defaultValue: view.email }]}
            footer={<button type="button" className="link" onClick={() => setView({ kind: "credentials" })}>Back to sign in</button>}
          />
        )}

        {view.kind === "forgot_confirm" && (
          <Form
            title="Check your e-mail"
            sub={`A code is on its way to ${view.email}. Enter it with your new password.`}
            busy={busy}
            error={error}
            submitLabel="Set the new password →"
            onSubmit={(d) =>
              run(async () => {
                if (d.password !== d.confirm) throw new Error("The two passwords do not match.");
                await confirmForgotPassword(c.cognito_region, c.cognito_client_id, view.email, d.code, d.password);
                setNotice("Password changed. Sign in with it.");
                setView({ kind: "credentials" });
              })
            }
            fields={[
              { name: "code", label: "Code", type: "text", autoComplete: "one-time-code", autoFocus: true, inputMode: "numeric" },
              { name: "password", label: "New password", type: "password", autoComplete: "new-password", hint: "At least 12 characters, with upper and lower case, a number and a symbol." },
              { name: "confirm", label: "Again, to be sure", type: "password", autoComplete: "new-password" },
            ]}
            footer={<button type="button" className="link" onClick={() => setView({ kind: "credentials" })}>Back to sign in</button>}
          />
        )}

        <p className="mt-8 text-center text-[11.5px] text-muted">
          Having trouble?{" "}
          <button type="button" className="link" onClick={() => void auth.login()}>
            Use the standard sign-in page
          </button>
        </p>
      </div>
    </div>
  );
}

interface FieldSpec {
  name: string;
  label: string;
  type: string;
  autoComplete?: string;
  autoFocus?: boolean;
  inputMode?: "numeric" | "text";
  hint?: string;
  defaultValue?: string;
}

function Form({
  title,
  sub,
  fields,
  submitLabel,
  onSubmit,
  busy,
  error,
  notice,
  footer,
  disabled,
}: {
  title: string;
  sub: string;
  fields: FieldSpec[];
  submitLabel: string;
  onSubmit: (data: Record<string, string>) => void;
  busy: boolean;
  error: string | null;
  notice?: string | null;
  footer?: ReactNode;
  disabled?: boolean;
}) {
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data: Record<string, string> = {};
    for (const f of fields) data[f.name] = String(fd.get(f.name) ?? "");
    onSubmit(data);
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
      <div>
        <h1 className="text-[22px] font-extrabold tracking-tight">{title}</h1>
        <p className="text-muted text-[13px] mt-1.5">{sub}</p>
      </div>
      {notice && <div className="rounded-[10px] bg-[#eaf7ef] text-[12.5px] text-ink px-3 py-2.5">{notice}</div>}
      {error && <div className="rounded-[10px] border border-[color:var(--status-high)] text-[12.5px] text-ink px-3 py-2.5">{error}</div>}
      {disabled && <div className="rounded-[10px] bg-tile text-[12.5px] text-muted px-3 py-2.5">Sign-in is not configured for this environment.</div>}
      {fields.map((f) => (
        <label key={f.name} className="block">
          <span className="block text-[12.5px] font-semibold text-ink mb-1.5">{f.label}</span>
          <input
            className="input !py-2.5 !px-3.5 !text-[14px]"
            name={f.name}
            type={f.type}
            autoComplete={f.autoComplete}
            autoFocus={f.autoFocus}
            inputMode={f.inputMode}
            defaultValue={f.defaultValue}
            required
            disabled={busy || disabled}
          />
          {f.hint && <span className="block text-[11.5px] text-muted mt-1.5">{f.hint}</span>}
        </label>
      ))}
      <Button type="submit" className="w-full justify-center !py-3 mt-1" disabled={busy || disabled}>
        {busy ? "One moment…" : submitLabel}
      </Button>
      {footer && <div className="text-center text-[12.5px]">{footer}</div>}
    </form>
  );
}
